import { useAppSSE } from 'contexts/AppSSEContext';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { organizationApi } from 'services/organization/organization.api';
import { rolesApi } from 'services/roles/roles.api';

const notifySseActivity =
  process.env.NODE_ENV === 'development'
    ? require('components/DevRenderOverlay/sseActivityHelper').notifySseActivity
    : () => {};

/**
 * A role definition changed somewhere in this workplace (created, permissions/name
 * edited, or deleted). Refetch the authoritative roles + members lists so the
 * roles-management admin UI reconciles without a manual reload — we invalidate the
 * RTK Query tags rather than trusting the pushed payload.
 *
 * Scope split: this handles the shared roles/members catalog. The current user's
 * own effective-permission refresh is handled separately inside PermsContext.
 *
 * The event is workplace-scoped and a single admin save can fire a burst of events,
 * so debounce to collapse them into one invalidation. If the roles screen isn't
 * mounted, invalidation is cheap — the queries simply refetch next time they mount.
 */
export default function useRolePermsEvents({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const { subscribe } = useAppSSE();

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    const unsubscribe = subscribe('role-perms-updated', (data) => {
      notifySseActivity('role-perms-updated');
      console.log('[role-perms-updated] received (catalog handler):', data);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        console.log('[role-perms-updated] invalidating Roles + Members tags', data);
        dispatch(rolesApi.util.invalidateTags(['Roles']));
        dispatch(organizationApi.util.invalidateTags(['Members']));
      }, 250);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [enabled, subscribe, dispatch]);
}
